/**
 * Builds a minimal ONNX model in memory: `output = input * scale`, a few hundred
 * bytes that run instantly.
 *
 * Two jobs.
 *
 * 1. **Keep-alive session.** ONNX Runtime only exposes its `GPUDevice` after the
 *    first WebGPU session exists, and destroys it when the last one is released.
 *    `initRuntime` therefore holds one session built from this model for the
 *    lifetime of the runtime, which is what keeps the shared device valid while
 *    the user swaps models.
 * 2. **Testing the tensor bridge.** `packTensor` -> `Tensor.fromGpuBuffer` ->
 *    inference -> `gpuBuffer` output -> `unpackTensor` is the most important
 *    integration in the project and the one a shader test cannot reach.
 *    Downloading a real 88 MB network to prove that plumbing works would be
 *    slow, flaky, and on a software adapter close to unusable. Doubling the
 *    input makes a wrong result unambiguous.
 *
 * ONNX is protobuf, so this is a small hand-rolled encoder. Field numbers are
 * from onnx.proto3.
 */

const FLOAT = 1;
const FLOAT16 = 10;

class Writer {
  #parts: Uint8Array[] = [];

  varint(value: number): this {
    let remaining = value;
    const bytes: number[] = [];
    do {
      let byte = remaining & 0x7f;
      remaining = Math.floor(remaining / 128);
      if (remaining > 0) byte |= 0x80;
      bytes.push(byte);
    } while (remaining > 0);
    this.#parts.push(new Uint8Array(bytes));
    return this;
  }

  /** varint field. */
  int(field: number, value: number): this {
    return this.varint((field << 3) | 0).varint(value);
  }

  /** length-delimited field. */
  bytes(field: number, payload: Uint8Array): this {
    this.varint((field << 3) | 2).varint(payload.length);
    this.#parts.push(payload);
    return this;
  }

  string(field: number, value: string): this {
    return this.bytes(field, new TextEncoder().encode(value));
  }

  message(field: number, writer: Writer): this {
    return this.bytes(field, writer.finish());
  }

  finish(): Uint8Array {
    const total = this.#parts.reduce((sum, part) => sum + part.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of this.#parts) {
      out.set(part, offset);
      offset += part.length;
    }
    return out;
  }
}

function tensorType(elemType: number, dims: readonly number[]): Writer {
  const shape = new Writer();
  for (const dim of dims) shape.message(1, new Writer().int(1, dim));
  const tensor = new Writer().int(1, elemType).message(2, shape);
  return new Writer().message(1, tensor);
}

function valueInfo(name: string, elemType: number, dims: readonly number[]): Writer {
  return new Writer().string(1, name).message(2, tensorType(elemType, dims));
}

/** fp32 -> IEEE half, for the float16 variant's scale initializer. */
function toHalf(value: number): number {
  const floats = new Float32Array(1);
  const ints = new Uint32Array(floats.buffer);
  floats[0] = value;
  const bits = ints[0]!;
  const sign = (bits >>> 16) & 0x8000;
  const exponent = ((bits >>> 23) & 0xff) - 127 + 15;
  const mantissa = bits & 0x7fffff;
  if (exponent <= 0) return sign;
  if (exponent >= 31) return sign | 0x7c00;
  return sign | (exponent << 10) | (mantissa >> 13);
}

export interface TinyModelOptions {
  channels?: number;
  height?: number;
  width?: number;
  scale?: number;
  dtype?: 'float32' | 'float16';
}

/**
 * `output = input * scale`, shaped `[1, channels, height, width]`.
 *
 * Returns the serialised ONNX model.
 */
export function buildScaleModel(options: TinyModelOptions = {}): ArrayBuffer {
  const channels = options.channels ?? 3;
  const height = options.height ?? 64;
  const width = options.width ?? 64;
  const scale = options.scale ?? 2;
  const fp16 = options.dtype === 'float16';
  const elemType = fp16 ? FLOAT16 : FLOAT;

  // Scalar initializer (no dims), so Mul broadcasts it over the whole tensor.
  const raw = fp16
    ? new Uint8Array(Uint16Array.of(toHalf(scale)).buffer)
    : new Uint8Array(Float32Array.of(scale).buffer);
  const initializer = new Writer().int(2, elemType).string(8, 'scale').bytes(9, raw);

  const node = new Writer()
    .string(1, 'input')
    .string(1, 'scale')
    .string(2, 'output')
    .string(3, 'scale_node')
    .string(4, 'Mul');

  const graph = new Writer()
    .message(1, node)
    .string(2, 'tiny')
    .message(5, initializer)
    .message(11, valueInfo('input', elemType, [1, channels, height, width]))
    .message(12, valueInfo('output', elemType, [1, channels, height, width]));

  const model = new Writer()
    .int(1, 8) // ir_version
    .string(2, 'design-ai-selftest')
    .message(7, graph)
    .message(8, new Writer().string(1, '').int(2, 13)); // opset ai.onnx v13

  const bytes = model.finish();
  // Copy out so the caller owns a plain ArrayBuffer.
  return bytes.slice().buffer;
}
